import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';

const LoginHosted: React.FC = () => {
  const { t } = useTranslation();
  const { getHostedUIUrl } = useAuth();

  const handleHostedUILogin = () => {
    // Cognito Hosted UIにリダイレクト
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || 'enterprise-auth-dev-auth';
    const region = import.meta.env.VITE_AWS_REGION || 'ap-northeast-1';
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
    
    // URLSearchParamsを使用して適切にパラメータを構築
    const params = new URLSearchParams();
    params.append('client_id', clientId);
    params.append('response_type', 'code');
    params.append('scope', 'email openid profile aws.cognito.signin.user.admin');
    params.append('redirect_uri', window.location.origin);
    
    const hostedUIUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/login?${params.toString()}`;
    
    // デバッグ情報をコンソールに出力
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MODE === 'true') {
      console.log('Hosted UI URL Generation:', {
        domain: cognitoDomain,
        region: region,
        clientId: clientId,
        redirectUri: window.location.origin,
        generatedUrl: hostedUIUrl
      });
    }
    
    window.location.href = hostedUIUrl;
  };

  const handleSSOLogin = (provider: string) => {
    // 特定のSSO プロバイダーでログイン
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || 'enterprise-auth-dev-auth';
    const region = import.meta.env.VITE_AWS_REGION || 'ap-northeast-1';
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
    
    // URLSearchParamsを使用して適切にパラメータを構築
    const params = new URLSearchParams();
    params.append('identity_provider', provider);
    params.append('client_id', clientId);
    params.append('response_type', 'code');
    params.append('scope', 'email openid profile aws.cognito.signin.user.admin');
    params.append('redirect_uri', window.location.origin);
    
    const ssoUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/authorize?${params.toString()}`;
    
    // デバッグ情報をコンソールに出力
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MODE === 'true') {
      console.log('SSO URL Generation:', {
        provider: provider,
        domain: cognitoDomain,
        region: region,
        clientId: clientId,
        redirectUri: window.location.origin,
        generatedUrl: ssoUrl
      });
    }
    
    window.location.href = ssoUrl;
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 450,
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}
        >
          {/* Logo/Title */}
          <Box textAlign="center" mb={4}>
            <SecurityIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              企業コンテンツポータル
            </Typography>
            <Typography variant="body1" color="text.secondary">
              セキュアな認証でアクセス
            </Typography>
          </Box>

          {/* Main Login Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleHostedUILogin}
            startIcon={<LoginIcon />}
            sx={{
              py: 2,
              mb: 3,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
              },
            }}
          >
            Cognito Hosted UI でログイン
          </Button>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              または企業SSO
            </Typography>
          </Divider>

          {/* SSO Options */}
          <Stack spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => handleSSOLogin('AzureAD')}
              startIcon={<BusinessIcon />}
              sx={{
                py: 1.5,
                borderColor: '#0078d4',
                color: '#0078d4',
                '&:hover': {
                  borderColor: '#106ebe',
                  backgroundColor: 'rgba(0, 120, 212, 0.04)',
                },
              }}
            >
              Azure Active Directory
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => handleSSOLogin('ADFS')}
              startIcon={<BusinessIcon />}
              sx={{
                py: 1.5,
                borderColor: '#00bcf2',
                color: '#00bcf2',
                '&:hover': {
                  borderColor: '#0099cc',
                  backgroundColor: 'rgba(0, 188, 242, 0.04)',
                },
              }}
            >
              Active Directory FS
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => handleSSOLogin('OKTA')}
              startIcon={<BusinessIcon />}
              sx={{
                py: 1.5,
                borderColor: '#007dc1',
                color: '#007dc1',
                '&:hover': {
                  borderColor: '#005a8b',
                  backgroundColor: 'rgba(0, 125, 193, 0.04)',
                },
              }}
            >
              Okta
            </Button>
          </Stack>

          {/* Info Section */}
          <Box mt={4} p={2} bgcolor="rgba(255, 255, 255, 0.7)" borderRadius={2}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <strong>セキュリティ機能:</strong>
              <br />
              • 多要素認証 (MFA)
              <br />
              • ドメイン制限
              <br />
              • 高度なセキュリティ機能
              <br />
              • 監査ログ
            </Typography>
          </Box>

          {/* Footer */}
          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              © 2024 Group Content Portal - Powered by AWS Cognito
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginHosted;
